import enUS from './en-US'
import jaJP from './ja-JP'
import zhCN from './zh-CN'
import type { BodyPart } from '../sim/JKGame_types' // type-only; sim never imports i18n (no cycle)
import { zhTW, type JKGameMsgKey } from './zh-TW'

// Multi-locale runtime. zh-TW is the complete master; other locales are partial
// overrides that fall back to it, so a raw key never reaches the screen.
export type { JKGameMsgKey }
export type Locale = 'zh-TW' | 'zh-CN' | 'ja-JP' | 'en-US'

export const LOCALES: ReadonlyArray<{ id: Locale; label: string }> = [
  { id: 'zh-TW', label: '繁體中文' },
  { id: 'zh-CN', label: '简体中文' },
  { id: 'ja-JP', label: '日本語' },
  { id: 'en-US', label: 'English' },
]

type Dict = Partial<Record<JKGameMsgKey, string>>
const DICTS: Record<Locale, Dict> = { 'zh-TW': zhTW, 'zh-CN': zhCN, 'ja-JP': jaJP, 'en-US': enUS }
const STORAGE_KEY = 'jkgame.locale'

function detectLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Locale | null
    if (saved && DICTS[saved]) return saved
  } catch {
    /* localStorage unavailable */
  }
  const n = (typeof navigator !== 'undefined' ? navigator.language : 'zh-TW').toLowerCase()
  if (n.startsWith('ja')) return 'ja-JP'
  if (n.startsWith('en')) return 'en-US'
  if (n === 'zh-cn' || n.includes('hans') || n === 'zh-sg') return 'zh-CN'
  return 'zh-TW'
}

let current: Locale = detectLocale()
const listeners = new Set<() => void>()

// BCP-47 tag for <html lang>, so screen readers announce content correctly.
const LANG_TAG: Record<Locale, string> = {
  'zh-TW': 'zh-Hant', 'zh-CN': 'zh-Hans', 'ja-JP': 'ja', 'en-US': 'en',
}
function syncDocumentLang(locale: Locale): void {
  try {
    document.documentElement.lang = LANG_TAG[locale]
  } catch {
    /* no document (tests / headless) */
  }
}
syncDocumentLang(current) // align first paint with the detected locale

export function getLocale(): Locale {
  return current
}

export function setLocale(locale: Locale): void {
  if (!DICTS[locale]) return
  current = locale
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    /* ignore */
  }
  syncDocumentLang(locale)
  for (const fn of [...listeners]) fn()
}

/** Subscribe to locale changes; returns an unsubscribe fn. */
export function onLocaleChange(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export type TParams = Record<string, string | number>

export function t(key: JKGameMsgKey, params?: TParams): string {
  const template = DICTS[current][key] ?? zhTW[key] ?? '（缺少翻譯）'
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (_m, name: string) => (name in params ? String(params[name]) : `{${name}}`))
}

// True when a key resolves in the active locale or the zh-TW master. Lets callers with
// content-driven (dynamic) keys degrade to a fallback instead of leaking a raw key.
export function hasMsg(key: string): boolean {
  return (DICTS[current][key as JKGameMsgKey] ?? zhTW[key as JKGameMsgKey]) !== undefined
}

// Localizes a loot-item id (see sim/JKGame_content LOOT_ITEMS) to a display name.
// Falls back to a generic localized label so a raw key never reaches the player.
export function itemName(id: string): string {
  const key = `item.${id}` as JKGameMsgKey
  const has = (DICTS[current][key] ?? zhTW[key]) !== undefined
  return has ? t(key) : t('item.generic')
}

// Localizes a run-buff (boon) id to its display name / description. Mirrors itemName: a `has`
// check falls back to a generic label so an unknown id never surfaces a raw `boon.<id>` key.
export function boonName(id: string): string {
  const key = `boon.${id}.name` as JKGameMsgKey
  const has = (DICTS[current][key] ?? zhTW[key]) !== undefined
  return has ? t(key) : t('boon.generic')
}
export function boonDesc(id: string): string {
  const key = `boon.${id}.desc` as JKGameMsgKey
  const has = (DICTS[current][key] ?? zhTW[key]) !== undefined
  return has ? t(key) : ''
}

// Localizes a season id (LiveOps-driven; e.g. 's1_tidefall') to its display name.
// Unknown ids fall back to a generic label so a raw key never reaches the player.
export function seasonName(id: string): string {
  const key = `season.${id}` as JKGameMsgKey
  const has = (DICTS[current][key] ?? zhTW[key]) !== undefined
  return has ? t(key) : t('season.unnamed')
}

// Weather index (the sim rolls 0..WEATHER_COUNT-1) → localized name. Mirrors itemName/
// seasonName: a `has` check falls back to a generic label so an out-of-range index can never
// surface a raw `weather.wN` key. Replaces two `weather.w${i} as JKGameMsgKey` casts.
export function weatherName(index: number): string {
  const key = `weather.w${index}` as JKGameMsgKey
  const has = (DICTS[current][key] ?? zhTW[key]) !== undefined
  return has ? t(key) : t('weather.unknown')
}

// Weapons carry only their canonical zh-TW display name (the content-pack is the
// single source both the local sim and backend replays emit). Map that name to a
// localized label so en/ja players don't see Chinese weapon names; unknown names
// fall back to the raw string (never a raw key, never empty).
const WEAPON_KEY: Readonly<Record<string, JKGameMsgKey>> = {
  空手: 'weapon.fists',
  金屬球棒: 'weapon.bat',
  柴刀: 'weapon.machete',
  軍刀: 'weapon.saber',
  十字弓: 'weapon.crossbow',
  麻醉飛鏢: 'weapon.dart',
  貝瑞塔手槍: 'weapon.beretta',
  'Uzi 衝鋒槍': 'weapon.uzi',
  霰彈槍: 'weapon.shotgun',
  手榴彈: 'weapon.grenade',
}
export function weaponName(name: string): string {
  const key = WEAPON_KEY[name]
  return key ? t(key) : name
}

// Injury body-part code (sim BodyPart: b/h/a/f) → localized label. Typed Record<BodyPart,…>
// so TS forces EVERY part to have a key — a new BodyPart can't compile without one. The
// `string` param covers the backend/local-unified injury sets (ReadonlySet<string>); an
// unexpected code falls back to the raw code (a single char, never a raw i18n key), mirroring
// weaponName. Replaces four scattered `part.${c} as JKGameMsgKey` casts.
const PART_KEY: Record<BodyPart, JKGameMsgKey> = {
  b: 'part.b',
  h: 'part.h',
  a: 'part.a',
  f: 'part.f',
}
export function partLabel(part: string): string {
  const key = PART_KEY[part as BodyPart]
  return key ? t(key) : part
}
